import {announce} from '@github-ui/aria-live'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import type {WorkflowRun} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {Link as RouterLink} from '@github-ui/react-router'
import type {QueryKey} from '@github-ui/react-query'
import {Link, SkeletonBox, Spinner, VisuallyHidden} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {useEffect, useRef, type ReactNode} from 'react'
import actionsIllustration from '../../assets/actions-icon-actions.svg'
import type {AutoRefreshOptionId} from './workflow-runs-auto-refresh'
import {WorkflowRunsAutoRefreshMenu} from './WorkflowRunsAutoRefreshMenu'
import {WorkflowRunsHeaderFilters} from './WorkflowRunsHeaderFilter'
import {WorkflowRunListItem} from './WorkflowRunListItem'
import styles from './WorkflowRunsList.module.css'

const SKELETON_ROW_COUNT = 10
interface WorkflowRunsListProps {
  workflowRuns: WorkflowRun[]
  accessibleName: string
  owner: string
  repo: string
  queryKey: QueryKey
  allRunsPath: string
  selectedWorkflow: boolean
  query?: string
  onQueryChange: (query: string) => void
  totalCount: number
  countIsCapped: boolean
  pageLimitReached: boolean
  pageLimitHeadingId?: string
  loading?: boolean
  showingPreviousResults?: boolean
  actionsEnabled?: boolean
  onFocusedRowRemoved?: () => void
  autoRefresh?: AutoRefreshOptionId
  onAutoRefreshChange?: (value: AutoRefreshOptionId) => void
  autoRefreshInactive?: boolean
  onRefreshNow?: () => void
}

export function WorkflowRunsList({
  workflowRuns,
  accessibleName,
  owner,
  repo,
  queryKey,
  allRunsPath,
  selectedWorkflow,
  query,
  onQueryChange,
  totalCount,
  countIsCapped,
  pageLimitReached,
  pageLimitHeadingId,
  loading = false,
  showingPreviousResults = false,
  actionsEnabled = true,
  onFocusedRowRemoved,
  autoRefresh,
  onAutoRefreshChange,
  autoRefreshInactive,
  onRefreshNow,
}: WorkflowRunsListProps) {
  const isInitialRenderRef = useRef(true)
  const showLoadingState = loading && workflowRuns.length === 0
  const showAutoRefresh = autoRefresh !== undefined && onAutoRefreshChange !== undefined && onRefreshNow !== undefined
  const showMetadata = !showLoadingState || showAutoRefresh
  const resultCount = `${totalCount}${countIsCapped ? '+' : ''}`
  const resultUnit = query?.trim() ? 'workflow run result' : 'workflow run'
  const resultCountLabel = `${resultCount} ${resultUnit}${totalCount === 1 ? '' : 's'}`
  const showPendingResults = loading && showingPreviousResults && workflowRuns.length > 0

  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Announce the initial rendered loading state.
      if (showLoadingState) {
        announce('Loading workflow runs')
      }
      return
    }

    if (loading) {
      announce('Loading workflow runs')
    } else if (pageLimitReached) {
      const pageLimitHeadingHasFocus =
        pageLimitHeadingId !== undefined && document.activeElement?.id === pageLimitHeadingId
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Announce page-limit transitions that do not receive focus.
      if (!pageLimitHeadingHasFocus) {
        announce('You have hit the limit of pages.')
      }
    } else if (workflowRuns.length === 0) {
      announce('No workflow runs found')
    } else {
      announce(workflowRuns.length === 1 ? '1 workflow run loaded' : `${workflowRuns.length} workflow runs loaded`)
    }
  }, [loading, pageLimitHeadingId, pageLimitReached, showLoadingState, workflowRuns.length])

  if (pageLimitReached) {
    return (
      <WorkflowRunsListEmptyState accessibleName={accessibleName}>
        <WorkflowRunsPageLimit headingId={pageLimitHeadingId} />
      </WorkflowRunsListEmptyState>
    )
  }

  return (
    <div className={styles.Container}>
      <ListView
        title={accessibleName}
        titleHeaderTag="h2"
        totalCount={totalCount}
        metadata={
          showMetadata ? (
            <ListViewMetadata
              className={styles.Metadata}
              title={resultCountLabel}
              trailingContent={
                <>
                  {showPendingResults && (
                    // Screen readers already hear "Loading workflow runs" from the announcement above.
                    <Spinner
                      size="small"
                      delay="short"
                      srText={null}
                      className={styles.PendingSpinner}
                      data-testid="workflow-runs-pending-spinner"
                    />
                  )}
                  {showAutoRefresh && (
                    <WorkflowRunsAutoRefreshMenu
                      value={autoRefresh}
                      onChange={onAutoRefreshChange}
                      onRefreshNow={onRefreshNow}
                      inactive={autoRefreshInactive}
                    />
                  )}
                </>
              }
              actionsLabel="Workflow run filters"
              actionBarItems={
                <WorkflowRunsHeaderFilters
                  owner={owner}
                  repo={repo}
                  query={query ?? ''}
                  selectedWorkflow={selectedWorkflow}
                  onQueryChange={onQueryChange}
                />
              }
            />
          ) : undefined
        }
        itemsListClassName={styles.Items}
        emptyState={
          <WorkflowRunsEmptyState allRunsPath={allRunsPath} query={query} selectedWorkflow={selectedWorkflow} />
        }
      >
        {showLoadingState
          ? Array.from({length: SKELETON_ROW_COUNT}, (_, index) => <WorkflowRunSkeleton key={index} />)
          : workflowRuns.map(run => (
              <WorkflowRunListItem
                key={run.id.toString()}
                run={run}
                queryKey={queryKey}
                actionsEnabled={actionsEnabled}
                onFocusedRowRemoved={onFocusedRowRemoved}
              />
            ))}
      </ListView>
      {showLoadingState && <VisuallyHidden>Loading workflow runs</VisuallyHidden>}
    </div>
  )
}

/** Renders a workflow-runs recovery state inside the standard ListView surface. */
export function WorkflowRunsListEmptyState({accessibleName, children}: {accessibleName: string; children: ReactNode}) {
  return (
    <div className={styles.Container}>
      <ListView
        title={accessibleName}
        titleHeaderTag="h2"
        totalCount={0}
        emptyState={<div className={styles.EmptyState}>{children}</div>}
      />
    </div>
  )
}

function WorkflowRunSkeleton() {
  return (
    <ListItem
      inert
      aria-hidden="true"
      className={styles.SkeletonListItem}
      metadataContainerClassName={styles.SkeletonMetadataContainer}
      title={
        <ListItemTitle value={<SkeletonBox height="var(--text-body-size-large)" className={styles.SkeletonTitle} />} />
      }
      metadata={
        <>
          <ListItemMetadata className={styles.SkeletonMetadata}>
            <SkeletonBox height="var(--text-body-size-medium)" className={styles.SkeletonBranch} />
          </ListItemMetadata>
          <ListItemMetadata className={styles.SkeletonTimingMetadata}>
            <SkeletonBox height="var(--text-body-size-medium)" className={styles.SkeletonDate} />
            <SkeletonBox height="var(--text-body-size-medium)" className={styles.SkeletonDuration} />
          </ListItemMetadata>
        </>
      }
    >
      <ListItemLeadingContent>
        <ListItemLeadingVisual>
          <SkeletonBox width="16px" height="16px" className={styles.SkeletonIcon} />
        </ListItemLeadingVisual>
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription className={styles.SkeletonDescription}>
          <SkeletonBox height="var(--text-body-size-medium)" className={styles.SkeletonWorkflowName} />
          <SkeletonBox height="var(--text-body-size-medium)" className={styles.SkeletonRunNumber} />
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

function WorkflowRunsEmptyState({
  allRunsPath,
  query,
  selectedWorkflow,
}: {
  allRunsPath: string
  query?: string
  selectedWorkflow: boolean
}) {
  if (query?.trim()) {
    return (
      <Blankslate className={styles.EmptyState}>
        <Blankslate.Visual>
          <img src={actionsIllustration} alt="" width={48} height={48} />
        </Blankslate.Visual>
        <Blankslate.Heading as="h2">No results matched your search.</Blankslate.Heading>
        <Blankslate.Description>
          You could search{' '}
          <Link as={RouterLink} inline to={allRunsPath}>
            all workflow runs
          </Link>{' '}
          or try different filters.
        </Blankslate.Description>
      </Blankslate>
    )
  }

  return (
    <Blankslate className={styles.EmptyState}>
      <Blankslate.Visual>
        <img src={actionsIllustration} alt="" width={48} height={48} />
      </Blankslate.Visual>
      <Blankslate.Heading as="h2">
        {selectedWorkflow ? 'This workflow has no runs yet.' : 'There are no workflow runs yet.'}
      </Blankslate.Heading>
    </Blankslate>
  )
}

function WorkflowRunsPageLimit({headingId}: {headingId?: string}) {
  return (
    <Blankslate>
      <Blankslate.Visual>
        <img src={actionsIllustration} alt="" width={48} height={48} />
      </Blankslate.Visual>
      <Blankslate.Heading as="h2" id={headingId} tabIndex={-1}>
        You have hit the limit of pages.
      </Blankslate.Heading>
    </Blankslate>
  )
}
